import { useCallback, useEffect, useRef, useState } from "react";

export type BreathingPhase = "inhale" | "hold" | "exhale" | "rest";

const PHASE_ORDER: BreathingPhase[] = ["inhale", "hold", "exhale", "rest"];

interface Options {
  cycles?: number;
  /** [inhale, hold, exhale, rest] in seconds */
  pattern?: [number, number, number, number];
}

export function useBreathingExercise({
  cycles = 5,
  pattern = [4, 4, 4, 4],
}: Options = {}) {
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [phaseCountdown, setPhaseCountdown] = useState(pattern[0]);
  const [currentCycle, setCurrentCycle] = useState(1);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const phase: BreathingPhase = PHASE_ORDER[phaseIndex];

  const clear = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isRunning || isComplete) {
      clear();
      return;
    }

    intervalRef.current = setInterval(() => {
      setPhaseCountdown((prev) => {
        if (prev > 1) return prev - 1;

        // Advance phase
        setPhaseIndex((idx) => {
          const nextIdx = (idx + 1) % PHASE_ORDER.length;
          if (nextIdx === 0) {
            // completed a cycle
            setCurrentCycle((c) => {
              if (c >= cycles) {
                setIsRunning(false);
                setIsComplete(true);
                return c;
              }
              return c + 1;
            });
          }
          // set countdown for the upcoming phase
          setPhaseCountdown(pattern[nextIdx]);
          return nextIdx;
        });

        return prev; // value will be overwritten above
      });
    }, 1000);

    return clear;
  }, [isRunning, isComplete, cycles, pattern, clear]);

  useEffect(() => () => clear(), [clear]);

  const start = useCallback(() => {
    if (isComplete) {
      setPhaseIndex(0);
      setPhaseCountdown(pattern[0]);
      setCurrentCycle(1);
      setIsComplete(false);
    }
    setIsRunning(true);
  }, [isComplete, pattern]);

  const pause = useCallback(() => setIsRunning(false), []);

  const reset = useCallback(() => {
    clear();
    setIsRunning(false);
    setIsComplete(false);
    setPhaseIndex(0);
    setPhaseCountdown(pattern[0]);
    setCurrentCycle(1);
  }, [clear, pattern]);

  return {
    phase,
    phaseCountdown,
    currentCycle,
    totalCycles: cycles,
    isRunning,
    isComplete,
    start,
    pause,
    reset,
  };
}
